(function () {
  'use strict';

  class VanGoghShopTheLook extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.root = this.closest('.vg-shop-look') || this;
      this.stage = this.querySelector('[data-vg-sl-stage]');
      this.items = Array.prototype.slice.call(this.querySelectorAll('[data-vg-sl-item]'));
      this.addAllBtn = this.querySelector('[data-vg-sl-add-all]');
      this.activeItem = null;
      this.closeTimer = null;
      this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      this.onDocClick = this.onDocClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onResize = this.debounce(this.repositionOpenCard.bind(this), 100);

      this.initAnimations();
      this.bindHotspots();
      this.bindAddAll();
      this.openDefaultCards();

      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKeyDown);
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      if (this.closeTimer) clearTimeout(this.closeTimer);
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKeyDown);
      window.removeEventListener('resize', this.onResize);
    }

    debounce(fn, wait) {
      var timer;
      return function () {
        var args = arguments;
        clearTimeout(timer);
        timer = setTimeout(function () {
          fn.apply(null, args);
        }, wait);
      };
    }

    initAnimations() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.root.classList.add('is-inview');
        return;
      }

      this._io = new IntersectionObserver(
        function (entries) {
          entries.forEach(
            function (entry) {
              if (!entry.isIntersecting) return;
              this.root.classList.add('is-inview');
              this._io.disconnect();
            }.bind(this)
          );
        }.bind(this),
        { threshold: 0.15 }
      );

      this._io.observe(this.root);
    }

    clearCloseTimer() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }

    scheduleClose(item) {
      this.clearCloseTimer();
      this.closeTimer = setTimeout(
        function () {
          if (this.activeItem !== item) return;
          this.closeItem(item);
        }.bind(this),
        120
      );
    }

    bindHotspots() {
      this.items.forEach(
        function (item) {
          var hotspot = item.querySelector('[data-vg-sl-hotspot]');
          var card = item.querySelector('[data-vg-sl-card]');
          var form = item.querySelector('[data-vg-sl-atc]');

          if (this.canHover && hotspot && card) {
            item.addEventListener(
              'mouseenter',
              function () {
                this.clearCloseTimer();
                this.openItem(item);
              }.bind(this)
            );

            item.addEventListener(
              'mouseleave',
              function (event) {
                var next = event.relatedTarget;
                if (next && item.contains(next)) return;
                this.scheduleClose(item);
              }.bind(this)
            );
          }

          if (hotspot) {
            hotspot.addEventListener(
              'click',
              function (event) {
                event.preventDefault();
                event.stopPropagation();
                if (this.canHover) return;
                this.toggleItem(item);
              }.bind(this)
            );

            hotspot.addEventListener(
              'focus',
              function () {
                this.clearCloseTimer();
                this.openItem(item);
              }.bind(this)
            );

            hotspot.addEventListener(
              'blur',
              function (event) {
                var next = event.relatedTarget;
                if (next && item.contains(next)) return;
                if (!this.canHover && item.getAttribute('data-vg-default-open') === 'true') return;
                this.closeItem(item);
              }.bind(this)
            );
          }

          if (card) {
            card.addEventListener(
              'focusin',
              function () {
                this.clearCloseTimer();
                this.openItem(item);
              }.bind(this)
            );

            card.addEventListener(
              'focusout',
              function (event) {
                var next = event.relatedTarget;
                if (next && item.contains(next)) return;
                if (!this.canHover && item.getAttribute('data-vg-default-open') === 'true') return;
                this.closeItem(item);
              }.bind(this)
            );
          }

          if (form) {
            form.addEventListener(
              'submit',
              function (event) {
                event.preventDefault();
                this.addToCart(form);
              }.bind(this)
            );
          }
        }.bind(this)
      );
    }

    openDefaultCards() {
      this.items.forEach(
        function (item) {
          if (item.getAttribute('data-vg-default-open') === 'true') {
            this.openItem(item);
          }
        }.bind(this)
      );
    }

    toggleItem(item) {
      if (this.activeItem === item) {
        this.closeItem(item);
        return;
      }
      this.openItem(item);
    }

    openItem(item) {
      if (this.activeItem && this.activeItem !== item) {
        this.closeItem(this.activeItem);
      }

      var hotspot = item.querySelector('[data-vg-sl-hotspot]');
      var card = item.querySelector('[data-vg-sl-card]');
      if (!hotspot || !card) return;

      var preferred = item.getAttribute('data-vg-tooltip-pos') || 'auto';
      var resolved = preferred === 'auto' ? this.resolvePosition(hotspot) : preferred;

      card.setAttribute('data-vg-pos', resolved);
      card.classList.add('is-open');
      hotspot.classList.add('is-active');
      hotspot.setAttribute('aria-expanded', 'true');
      this.activeItem = item;

      requestAnimationFrame(
        function () {
          this.keepCardInView(card);
        }.bind(this)
      );
    }

    closeItem(item) {
      if (!item) return;
      var hotspot = item.querySelector('[data-vg-sl-hotspot]');
      var card = item.querySelector('[data-vg-sl-card]');
      if (card) {
        card.classList.remove('is-open');
        card.style.translate = '';
      }
      if (hotspot) {
        hotspot.classList.remove('is-active');
        hotspot.setAttribute('aria-expanded', 'false');
      }
      if (this.activeItem === item) this.activeItem = null;
    }

    closeAll() {
      this.items.forEach(
        function (item) {
          if (!this.canHover && item.getAttribute('data-vg-default-open') === 'true') {
            this.openItem(item);
            return;
          }
          this.closeItem(item);
        }.bind(this)
      );
    }

    resolvePosition(hotspot) {
      if (!this.stage) return 'top';

      var stageRect = this.stage.getBoundingClientRect();
      var spotRect = hotspot.getBoundingClientRect();
      var xRatio = (spotRect.left + spotRect.width / 2 - stageRect.left) / stageRect.width;
      var yRatio = (spotRect.top + spotRect.height / 2 - stageRect.top) / stageRect.height;

      if (xRatio > 0.72) return 'left';
      if (xRatio < 0.28) return 'right';
      if (yRatio < 0.35) return 'bottom';
      return 'top';
    }

    keepCardInView(card) {
      var rect = card.getBoundingClientRect();
      var pad = 12;
      var shiftX = 0;
      var shiftY = 0;

      if (rect.left < pad) shiftX = pad - rect.left;
      if (rect.right > window.innerWidth - pad) shiftX = window.innerWidth - pad - rect.right;
      if (rect.top < pad) shiftY = pad - rect.top;
      if (rect.bottom > window.innerHeight - pad) shiftY = window.innerHeight - pad - rect.bottom;

      if (shiftX || shiftY) {
        card.style.translate = shiftX + 'px ' + shiftY + 'px';
      } else {
        card.style.translate = '';
      }
    }

    repositionOpenCard() {
      if (!this.activeItem) return;
      var hotspot = this.activeItem.querySelector('[data-vg-sl-hotspot]');
      var card = this.activeItem.querySelector('[data-vg-sl-card]');
      if (!hotspot || !card) return;

      var preferred = this.activeItem.getAttribute('data-vg-tooltip-pos') || 'auto';
      var resolved = preferred === 'auto' ? this.resolvePosition(hotspot) : preferred;
      card.setAttribute('data-vg-pos', resolved);
      card.style.translate = '';
      this.keepCardInView(card);
    }

    onDocClick(event) {
      if (!this.contains(event.target)) {
        this.closeAll();
      }
    }

    onKeyDown(event) {
      if (event.key === 'Escape') this.closeAll();
    }

    cartAddUrl() {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        return window.Shopify.routes.root + 'cart/add.js';
      }
      return '/cart/add.js';
    }

    refreshCartUI() {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
      if (window.theme && typeof window.theme.openCartDrawer === 'function') {
        window.theme.openCartDrawer();
      }
    }

    bindAddAll() {
      if (!this.addAllBtn) return;
      this.addAllBtn.addEventListener(
        'click',
        function () {
          this.addAllToCart();
        }.bind(this)
      );
    }

    addAllToCart() {
      if (!this.addAllBtn) return;

      var raw = this.addAllBtn.getAttribute('data-vg-sl-variant-ids') || '';
      var ids = raw
        .split(',')
        .map(function (id) {
          return parseInt(id, 10);
        })
        .filter(function (id) {
          return !!id;
        });

      if (!ids.length) return;

      var items = ids.map(function (id) {
        return { id: id, quantity: 1 };
      });

      this.addAllBtn.classList.add('is-loading');
      this.addAllBtn.disabled = true;

      fetch(this.cartAddUrl(), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: items }),
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Add all failed');
          return response.json();
        })
        .then(
          function () {
            this.refreshCartUI();
          }.bind(this)
        )
        .catch(function () {})
        .finally(
          function () {
            this.addAllBtn.classList.remove('is-loading');
            this.addAllBtn.disabled = false;
          }.bind(this)
        );
    }

    addToCart(form) {
      var button = form.querySelector('[type="submit"]');
      var formData = new FormData(form);

      if (button) button.classList.add('is-loading');

      fetch(this.cartAddUrl(), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Add to cart failed');
          return response.json();
        })
        .then(
          function () {
            this.refreshCartUI();
          }.bind(this)
        )
        .catch(function () {
          form.submit();
        })
        .finally(function () {
          if (button) button.classList.remove('is-loading');
        });
    }
  }

  if (!customElements.get('van-gogh-shop-the-look')) {
    customElements.define('van-gogh-shop-the-look', VanGoghShopTheLook);
  }
})();
